/**
 * AwwaM37Toggle.tsx — Global AWWA M37 override switch
 *
 * The AWWA M37 (2011) standard caps reservoir aeration module density
 * at 1–6 modules per hectare. By default the tool enforces this band on
 * every coverage / validation / comparison page.
 *
 * Toggle this OFF when you need to size the array for full coverage of
 * a particularly demanding reservoir (high SOD, complex geometry, strong
 * stratification) without being limited by the AWWA cap. Every affected
 * page recomputes live — module count, coverage %, PASS/FAIL flags,
 * recommendations and visuals all switch in step.
 *
 * The override state is persisted to localStorage so it survives reloads
 * and propagates across tabs via the `rb-persist-changed` event.
 */

import { useEffect, useState } from 'react';
import { Shield, ShieldOff } from 'lucide-react';
import { loadPersisted, savePersisted, onPersistChange } from '@/lib/sessionPersistence';

export const AWWA_OVERRIDE_KEY = 'rb_awwa_override_v1';

interface AwwaState { enabled: boolean }

const DEFAULT_STATE: AwwaState = { enabled: false };

/** Hook returning the current override state and a toggle function. */
export function useAwwaOverride(): { enabled: boolean; toggle: () => void; setEnabled: (v: boolean) => void } {
  const [enabled, setEnabledState] = useState<boolean>(
    () => loadPersisted<AwwaState>(AWWA_OVERRIDE_KEY, DEFAULT_STATE).enabled
  );

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === AWWA_OVERRIDE_KEY) {
        setEnabledState(loadPersisted<AwwaState>(AWWA_OVERRIDE_KEY, DEFAULT_STATE).enabled);
      }
    });
    return () => unsub();
  }, []);

  const setEnabled = (v: boolean) => {
    savePersisted(AWWA_OVERRIDE_KEY, { enabled: v });
    setEnabledState(v);
  };
  const toggle = () => setEnabled(!enabled);
  return { enabled, toggle, setEnabled };
}

interface Props {
  /** Compact rendering — narrower button only. */
  compact?: boolean;
}

export default function AwwaM37Toggle({ compact = false }: Props) {
  const { enabled, toggle } = useAwwaOverride();

  return (
    <button
      onClick={toggle}
      title={enabled ? 'Click to ENFORCE AWWA M37 (1–6 modules/ha cap)' : 'Click to BYPASS AWWA M37 (no module-density cap)'}
      className={`group inline-flex items-center gap-2 rounded-lg border-2 transition-all ${
        compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
      } ${
        enabled
          ? 'border-amber-600 bg-amber-900/30 text-amber-200 hover:bg-amber-900/50'
          : 'border-emerald-600 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50'
      }`}
    >
      {enabled ? <ShieldOff className="w-4 h-4 shrink-0" /> : <Shield className="w-4 h-4 shrink-0" />}
      <span className="font-bold">AWWA M37:</span>
      <span className={enabled ? 'text-amber-100' : 'text-emerald-100'}>
        {enabled ? 'BYPASSED' : 'ENFORCED'}
      </span>
      <span className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
        enabled ? 'bg-amber-600' : 'bg-emerald-600'
      }`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
          enabled ? 'right-0.5' : 'left-0.5'
        }`} />
      </span>
    </button>
  );
}

/** Constants used everywhere AWWA is referenced. */
export const AWWA_MIN_PER_HA = 1;
export const AWWA_MAX_PER_HA = 6;

/** Helper for module-count classification. Returns PASS when override is on. */
export function awwaClassify(moduleCount: number, reservoirAreaHa: number, override: boolean):
  { ok: boolean; min: number; max: number; verdict: 'pass' | 'low' | 'high' | 'bypass' } {
  const min = Math.ceil(AWWA_MIN_PER_HA * reservoirAreaHa);
  const max = Math.ceil(AWWA_MAX_PER_HA * reservoirAreaHa);
  if (override) return { ok: true, min, max, verdict: 'bypass' };
  if (moduleCount < min) return { ok: false, min, max, verdict: 'low' };
  if (moduleCount > max) return { ok: false, min, max, verdict: 'high' };
  return { ok: true, min, max, verdict: 'pass' };
}
